<!--
    REV-21364 — the Korrekturbuchungen of ONE account (Pure View), ADR-0015 §9 / §10.

    Layout, ordering and German labels follow the prototype's account area `Korrekturbuchungen`: pill
    year tabs with an `n offen` badge, then a table of eight columns.

    ⚠️ The table carries the shared ABCP table look (`styles/_abcp-table.scss`, compact) so it reads
    like every other ABCP table. The COMPONENT is not shared with the closing-wide list, and §9 says why.
-->
<div class="acc-adj" data-testid="account-adjustments">
    <!--
        The year tabs (AC5). Real buttons rather than a `select`: the prototype has pills, and pills are
        keyboard-reachable for free, which AC9 requires.
    -->
    <div class="acc-adj-years" role="tablist" [attr.aria-label]="'abcp_adjustments_fiscal_year' | i18next">
        <button
            type="button"
            class="acc-adj-year"
            data-testid="account-adjustments-year"
            role="tab"
            *ngFor="let year of fiscalYears; trackBy: trackBySelf"
            [attr.data-year]="year"
            [class.is-active]="isSelectedYear(year)"
            [attr.aria-selected]="isSelectedYear(year)"
            (click)="onYearClick(year)"
        >
            {{ 'abcp_adjustments_fiscal_year_short' | i18next }} {{ year }}
        </button>

        <!--
            `n offen` (AC2) — and GONE at zero rather than rendered as "0 offen". The badge answers "is
            there work here"; a zero is the answer "no", and nothing is the honest way to render it.
        -->
        <span class="acc-adj-badge" data-testid="account-adjustments-open-badge" *ngIf="showOpenBadge">
            {{ 'abcp_adjustments_open_count' | i18next: { count: openCount } }}
        </span>

        <!--
            Enter a correction from here (§10: current year only).

            Same label as the closing-wide register's control, because it opens the same dialog — a
            second wording would suggest a second kind of correction. It sits at the END of the tab row
            so the year pills keep the leading position they have in the prototype.
        -->
        <!-- Deliberately the same button as the closing-wide register's: it opens the same dialog. -->
        <ham-button
            class="acc-adj-create"
            data-testid="account-adjustments-create"
            variant="primary"
            size="toolbar"
            icon="assets/hamIcons/plus-outline.svg"
            *ngIf="canCreate"
            (buttonClick)="onCreateClick()"
        >
            {{ 'abcp_adjustments_new' | i18next }}
        </ham-button>
    </div>

    <div class="acc-adj-scroll">
        <table class="acc-adj-table" data-testid="account-adjustments-table">
            <thead>
                <tr>
                    <th class="acc-adj-th acc-adj-th--text" data-testid="account-adjustments-column" scope="col">
                        {{ 'abcp_ledger_col_text' | i18next }}
                    </th>
                    <th class="acc-adj-th acc-adj-th--gk" data-testid="account-adjustments-column" scope="col">
                        {{ 'abcp_ledger_col_account' | i18next }}
                    </th>
                    <th class="acc-adj-th acc-adj-th--gk-name" data-testid="account-adjustments-column" scope="col">
                        {{ 'abcp_ledger_col_account_label' | i18next }}
                    </th>
                    <th class="acc-adj-th acc-adj-th--value" data-testid="account-adjustments-column" scope="col">
                        {{ 'abcp_ledger_col_debit' | i18next }}
                    </th>
                    <th class="acc-adj-th acc-adj-th--value" data-testid="account-adjustments-column" scope="col">
                        {{ 'abcp_ledger_col_credit' | i18next }}
                    </th>
                    <th class="acc-adj-th acc-adj-th--status" data-testid="account-adjustments-column" scope="col">
                        {{ 'abcp_adjustments_col_state' | i18next }}
                    </th>
                </tr>
            </thead>

            <tbody>
                <!-- Loading: grey placeholders in the SHAPE of the list (AC7). -->
                <tr *ngIf="showSkeleton">
                    <td class="acc-adj-state" [attr.colspan]="columnCount">
                        <div class="acc-adj-loading" data-testid="account-adjustments-loading">
                            <div
                                class="acc-adj-skeleton"
                                *ngFor="let row of skeletonRows; trackBy: trackBySelf"
                                aria-hidden="true"
                            ></div>
                        </div>
                    </td>
                </tr>

                <!-- Error: a short note; the year tabs stay usable (AC7). -->
                <tr *ngIf="!loading && error">
                    <td class="acc-adj-state" [attr.colspan]="columnCount">
                        <div class="acc-adj-error" data-testid="account-adjustments-error">
                            <span>{{ 'abcp_adjustments_error' | i18next }}</span>
                            <button
                                type="button"
                                class="acc-adj-link"
                                data-testid="account-adjustments-retry"
                                (click)="onRetryClick()"
                            >
                                {{ 'abcp_adjustments_retry' | i18next }}
                            </button>
                        </div>
                    </td>
                </tr>

                <!-- Empty: `Keine Korrekturbuchungen für GJ <Jahr>.` — with the chosen year IN the sentence. -->
                <tr *ngIf="!loading && !error && isEmpty">
                    <td class="acc-adj-state" [attr.colspan]="columnCount">
                        <div class="acc-adj-empty" data-testid="account-adjustments-empty">
                            {{ 'abcp_adjustments_account_empty' | i18next: { year: selectedFiscalYear } }}
                        </div>
                    </td>
                </tr>

                <tr
                    class="acc-adj-row"
                    data-testid="account-adjustments-row"
                    *ngFor="let row of adjustments; trackBy: trackById"
                    [attr.data-adjustment-id]="row.id"
                    [attr.data-state]="row.state"
                    [attr.data-source]="row.source"
                    tabindex="0"
                    (click)="onRowClick(row)"
                    (keydown.enter)="onRowClick(row)"
                    (keydown.space)="onRowClick(row); $event.preventDefault()"
                >
                    <!--
                        The booking text, truncated with the full value in the tooltip (AC7): the pane is
                        narrow and the page must NEVER scroll horizontally.

                        The source marking sits inside this cell, as the prototype has it — and it is a
                        TEXT badge, never colour alone (AC4/AC9). `VJ` on a prior-year row; the reserved
                        `Agent` marking is styled and never produced (§8).
                    -->
                    <td
                        class="acc-adj-td acc-adj-td--text"
                        data-testid="account-adjustments-cell"
                        [hamTooltip]="row.text"
                        tooltipClass="abcp"
                        [tooltipOnlyWhenTruncated]="true"
                    >
                        <span
                            class="acc-adj-source"
                            data-testid="account-adjustments-source"
                            *ngIf="row.source !== 'user'"
                            [attr.data-source]="row.source"
                            >{{ sourceLabelKey(row.source) | i18next }}</span
                        >
                        <span class="acc-adj-text">{{ row.text }}</span>
                    </td>
                    <td class="acc-adj-td acc-adj-td--gk" data-testid="account-adjustments-cell">
                        {{ counterAccount(row).number }}
                    </td>
                    <td
                        class="acc-adj-td acc-adj-td--gk-name"
                        data-testid="account-adjustments-cell"
                        [hamTooltip]="counterAccount(row).name"
                        tooltipClass="abcp"
                        [tooltipOnlyWhenTruncated]="true"
                    >
                        {{ counterAccount(row).name }}
                    </td>
                    <!--
                        `Soll` / `Haben` — and the cell the correction does NOT sit on stays visually
                        EMPTY (AC9). A `0.00` there would destroy the one distinction the two columns
                        exist for.
                    -->
                    <td class="acc-adj-td acc-adj-td--value" data-testid="account-adjustments-cell">
                        <ng-container *ngIf="debitAmount(row) !== null">{{
                            debitAmount(row)! / 100 | finNumber: '1.2-2'
                        }}</ng-container>
                    </td>
                    <td class="acc-adj-td acc-adj-td--value" data-testid="account-adjustments-cell">
                        <ng-container *ngIf="creditAmount(row) !== null">{{
                            creditAmount(row)! / 100 | finNumber: '1.2-2'
                        }}</ng-container>
                    </td>
                    <td class="acc-adj-td acc-adj-td--status" data-testid="account-adjustments-cell">
                        <div class="acc-adj-status" (click)="$event.stopPropagation()">
                            <abcp-adjustment-status
                                [state]="row.state"
                                [bookedYear]="bookedYearOf(row)"
                                [ambiguous]="!!row.ambiguous"
                                [notFound]="!!row.notFound"
                                size="sm"
                            ></abcp-adjustment-status>
                            <!--
                                Discarding, `Offen` only (AC6) — and absent for every row in a prior year.
                                No edit and no transfer here: those stay on the closing-wide surface
                                (REV-21370). ENTERING is offered, by the button above the table — a
                                correction that belongs to this account is most naturally written while
                                looking at it.
                            -->
                            <ham-button
                                data-testid="account-adjustments-discard"
                                *ngIf="canDiscard(row)"
                                variant="red"
                                [outline]="true"
                                size="toolbar"
                                [hideText]="true"
                                icon="assets/hamIcons/x.svg"
                                [hamTooltip]="'abcp_adjustments_discard' | i18next"
                                tooltipClass="abcp"
                                (buttonClick)="onDiscardClick(row)"
                            ></ham-button>
                        </div>
                    </td>
                </tr>
            </tbody>
        </table>
    </div>
</div>
